import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { apiClient, ensureCsrfCookie, onUnauthorized } from '@/lib/api-client';
import { setSentryUser } from '@/lib/sentry';
import type { components } from '@/api/schema';

export type RoleName = 'owner' | 'manager' | 'cashier' | 'viewer';
export type UserStatus = 'pending' | 'active' | 'blocked';

export type MerchantPlatformStatus = 'new' | 'active' | 'blocked' | 'frozen' | 'pending' | 'rejected';

export interface AuthUser {
  id: number;
  phone: string | null;
  email: string | null;
  full_name: string;
  role: RoleName;
  status: UserStatus;
  merchant_id: number | null;
  /** The branch the staff member works at (profiles.branch_id in Lovable). */
  branch_id: number | null;
  merchant_platform_status: MerchantPlatformStatus | null;
  merchant_admin_comment: string | null;
  /** FR-96: set on an invited account until the temporary password is replaced. */
  must_change_password: boolean;
  /** Holds a platform role — Lovable's /login sends such a user to /admin/dashboard. */
  is_platform_admin: boolean;
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  login: (identifier: string, password: string) => Promise<AuthUser | null>;
  logout: () => Promise<void>;
  hasRole: (...roles: RoleName[]) => boolean;
  canAccess: (feature: string) => boolean;
  refreshUser: () => Promise<AuthUser | null>;
}

const AuthContext = createContext<AuthContextType | null>(null);

const ROLE_PERMISSIONS: Record<RoleName, string[]> = {
  owner: ['*'],
  manager: ['dashboard', 'sales', 'products', 'inventory', 'murabaha', 'reports', 'branches', 'devices', 'users', 'settings', 'pricing', 'customers', 'connectors', 'modules'],
  cashier: ['pos', 'murabaha', 'sales_own', 'customers'],
  viewer: ['dashboard', 'sales', 'products', 'inventory', 'reports'],
};

/** Highest-priority role wins, matching `AppRole::priority()` on the backend. */
const ROLE_PRIORITY: RoleName[] = ['owner', 'manager', 'cashier', 'viewer'];

function highestRole(roles: string[]): RoleName {
  return ROLE_PRIORITY.find((role) => roles.includes(role)) ?? 'viewer';
}

function toAuthUser(resource: components['schemas']['UserResource']): AuthUser {
  return {
    id: resource.id,
    phone: resource.phone,
    email: resource.email,
    full_name: resource.name,
    role: highestRole(resource.roles),
    status: resource.status as UserStatus,
    merchant_id: resource.merchant_id,
    branch_id: resource.branch_id ?? null,
    merchant_platform_status: resource.merchant_platform_status as MerchantPlatformStatus | null,
    merchant_admin_comment: resource.merchant_admin_comment,
    must_change_password: resource.must_change_password,
    is_platform_admin: (resource.platform_roles ?? []).length > 0,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    const { data, error, response } = await apiClient.GET('/me');
    if (error || !data) {
      // A transient 5xx keeps the already-resolved user; anything else (401…)
      // logs out (Lovable AuthContext, 2026-09-25 delta).
      if ((response?.status ?? 0) < 500) {
        setUser(null);
        setSentryUser(null);
      }
      return null;
    }
    const authUser = toAuthUser(data.data);
    setUser(authUser);
    setSentryUser({ id: authUser.id, role: authUser.role, merchant_id: authUser.merchant_id });
    return authUser;
  }, []);

  useEffect(() => {
    let mounted = true;

    void (async () => {
      try {
        await refreshUser();
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    const unsubscribe = onUnauthorized(() => {
      if (mounted) setUser(null);
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [refreshUser]);

  const login = useCallback(async (identifier: string, password: string) => {
    await ensureCsrfCookie();
    const { error } = await apiClient.POST('/login', { body: { identifier, password } });
    if (error) throw error;
    return refreshUser();
  }, [refreshUser]);

  const logout = useCallback(async () => {
    await apiClient.POST('/logout');
    setUser(null);
    setSentryUser(null);
  }, []);

  const hasRole = useCallback((...roles: RoleName[]) => {
    return user ? roles.includes(user.role) : false;
  }, [user]);

  const canAccess = useCallback((feature: string) => {
    if (!user) return false;
    const perms = ROLE_PERMISSIONS[user.role];
    return perms.includes('*') || perms.includes(feature);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, hasRole, canAccess, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
